import type { FC } from "react";

import { motion } from "framer-motion";

import { ActorLevelChip } from "./ActorLevelChip";
import {
  type BlackboardInstance,
  getInstanceDisplayState,
  getInstanceDisplayStyle,
} from "./blackboardModels";
import { getAvatarBackgroundColor, InstanceAvatar } from "./InstanceAvatar";
import { ModelChip } from "./ModelChip";
import { ReasoningEffortChip } from "./ReasoningEffortChip";

interface ActorPreviewCardProps {
  instance: BlackboardInstance;
}

const containerVariants = {
  hidden: { opacity: 0, scale: 0.95, y: 6 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      damping: 28,
      delayChildren: 0.04,
      mass: 0.6,
      staggerChildren: 0.05,
      stiffness: 400,
      type: "spring",
    },
    y: 0,
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 4 },
  visible: {
    opacity: 1,
    transition: { damping: 25, stiffness: 400, type: "spring" },
    y: 0,
  },
};

/** Compact HUD-CRT mini readout shown on hover over a stacked actor avatar.
 * Spring physics + stagger pattern lifted from 21st Profile Card (stiffness
 * 400 / damping 28 / mass 0.6) — dialed down to a 220px tactical readout
 * with corner brackets, scanline overlay, and the canonical `[ LABEL ]`
 * header. The visual shell of the 21st reference was discarded; we kept
 * only the motion primitives.
 *
 * Accent color is derived per-actor from getAvatarBackgroundColor(seed) so
 * each preview wears the same hue as its avatar — emerald / violet / amber
 * / rose / slate from the DiceBear theme palette.
 */
export const ActorPreviewCard: FC<ActorPreviewCardProps> = ({ instance }) => {
  const displayState = getInstanceDisplayState(instance);
  const displayStyle = getInstanceDisplayStyle(displayState);
  const accent = getAvatarBackgroundColor(instance.avatarSeed);
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      style={{ borderColor: `${accent}4d` }}
      className="relative w-56 border bg-white/95 shadow-2xl backdrop-blur-sm dark:bg-slate-950/95"
    >
      <span
        aria-hidden
        style={{ borderColor: `${accent}99` }}
        className="absolute -left-px -top-px h-2 w-2 border-l border-t"
      />
      <span
        aria-hidden
        style={{ borderColor: `${accent}99` }}
        className="absolute -right-px -top-px h-2 w-2 border-r border-t"
      />
      <span
        aria-hidden
        style={{ borderColor: `${accent}99` }}
        className="absolute -bottom-px -left-px h-2 w-2 border-b border-l"
      />
      <span
        aria-hidden
        style={{ borderColor: `${accent}99` }}
        className="absolute -bottom-px -right-px h-2 w-2 border-b border-r"
      />
      <div
        aria-hidden
        style={{
          background: `linear-gradient(to bottom, transparent 50%, ${accent}0d 50%)`,
          backgroundSize: "100% 3px",
        }}
        className="pointer-events-none absolute inset-0"
      />

      <motion.div
        variants={itemVariants}
        style={{ borderColor: `${accent}33` }}
        className="border-b px-3 py-1.5"
      >
        <p
          className="font-mono text-[9px] uppercase tracking-[0.22em]"
          style={{ color: `${accent}b3` }}
        >
          [ ACTOR READOUT ]
        </p>
      </motion.div>

      <div className="relative space-y-2 p-3">
        <motion.div
          variants={itemVariants}
          className="flex items-start gap-2.5"
        >
          <InstanceAvatar
            seed={instance.avatarSeed}
            size="md"
            status={instance.status}
            animated
          />
          <div className="min-w-0 flex-1">
            <p className="truncate font-mono text-sm font-semibold text-slate-900 dark:text-white">
              {instance.displayName}
            </p>
            <p
              className={`mt-0.5 font-mono text-[9px] font-bold uppercase tracking-widest ${displayStyle.textClass}`}
              title={displayStyle.tooltip ?? undefined}
            >
              {displayState}
            </p>
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="flex flex-wrap items-center gap-1"
        >
          <ActorLevelChip level={instance.evolution} />
          {instance.modelRef && <ModelChip modelRef={instance.modelRef} />}
          {instance.overrides?.reasoningEffort && (
            <ReasoningEffortChip
              compact
              override={instance.overrides.reasoningEffortOverride}
              reasoningEffort={instance.overrides.reasoningEffort}
              source={instance.overrides.reasoningEffortSource}
            />
          )}
        </motion.div>

        <motion.div
          variants={itemVariants}
          style={{ borderColor: `${accent}1a` }}
          className="grid grid-cols-2 gap-x-3 gap-y-1 border-t pt-2"
        >
          <div>
            <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-500">
              Channels
            </p>
            <p className="font-mono text-[11px] tabular-nums text-slate-800 dark:text-slate-200">
              {instance.channels.length}
            </p>
          </div>
          <div>
            <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-slate-500">
              Runs
            </p>
            <p className="font-mono text-[11px] tabular-nums text-slate-800 dark:text-slate-200">
              {instance.runCount}
            </p>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};
